import { Component, OnInit, Input, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ActivityService } from 'app/services/activity.service';
import { AgencyInfoService } from 'app/services/agency-info.service';
import { AppinfoService, GeneralColType } from 'app/services/appinfo.service';
import { ArchiveModeService } from 'app/services/archive-mode.service';
import { AttrIndexService } from 'app/services/attr-index.service';
import { AttributeFilterService } from 'app/services/attribute-filter.service';
import { CommonFunctionService } from 'app/services/common-function.service';
import { CommonTableService } from 'app/services/common-table.service';
import { DomainService, GivColumnDomain } from 'app/services/domain.service';
import { EditService } from 'app/services/edit.service';
import { LayerCategoryStandardSchema } from 'app/services/field-mapping.service';
import { GtoService } from 'app/services/gto.service';
import { LayerQcService, QcEditFilter } from 'app/services/layer-qc.service';
import { AppLayer, LayerService } from 'app/services/layer.service';
import { MainDataMapService } from 'app/services/main-data-map.service';
import { MainDataService } from 'app/services/main-data.service';
import { UserService } from 'app/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

export interface BatchEditableColumn {
	type: GeneralColType;
	prop: string;
	name: string;
	standardName: string;
	// domain?: GivColumnDomain;
	hideFromEditing?: boolean;
	isUppercase?: boolean;
}
export interface ClauseItem {
	col: BatchEditableColumn;
	operator: '=';
	val;
	isNew?: boolean;
	showDomain?: boolean;
	isValid?: boolean;
}

export interface FilterType {
	label: string;
	type: 'filter' | 'selection';
	count: number;
}

@Component({
	selector: 'app-edit-batch',
	templateUrl: './edit-batch.component.html',
	styleUrls: ['./edit-batch.component.css', '../edit-card/edit-card.component.css']
})
export class EditBatchComponent implements OnInit, OnDestroy, OnChanges {

	@Input() columns: BatchEditableColumn[] = [];

	// @Output() clickCommit: EventEmitter<any> = new EventEmitter();

	editClauses: ClauseItem[] = [];
	validEditClauses: ClauseItem[] = [];
	// isValid: boolean;
	hasChanged = false;
	commitDisableReason = '';

	filterTypes: FilterType[] = [
		{
			label: 'Filter Count',
			type: 'filter',
			count: 0
		}, {
			label: 'Select Count',
			type: 'selection',
			count: 0
		}
	];
	selectedFilterType: FilterType;

	domainsByColumn: {
		[prop: string]: GivColumnDomain;
	} = {};

	nguidStandardName: string;
	discrepancyIdStandardNameLower: string;

	private batchFilterCountSub: Subscription;

	constructor(
		private toastr: ToastrService,
		private activityService: ActivityService,
		private appinfoService: AppinfoService,
		private archiveModeService: ArchiveModeService,
		private agencyInfoService: AgencyInfoService,
		private attrFilterService: AttributeFilterService,
		private attrIndexService: AttrIndexService,
		private commonService: CommonFunctionService,
		private domainService: DomainService,
		private editService: EditService,
		private gtoService: GtoService,
		private layerService: LayerService,
		private qcService: LayerQcService,
		private mapService: MainDataMapService,
		private mainDataService: MainDataService,
		private tableService: CommonTableService,
		private userService: UserService
	) { }

	checkDiscardDisable() {
		if (this.editClauses.length === 0) {
			return true;
		}
		if (this.editClauses.length === 1) {
			return this.editClauses[0].isNew;
		}
	}
	checkCommitDisabled() {
		// Disable if no clause
		if (this.editClauses.length <= 0 || !this.editClauses.find(c => !c.isNew)) {
			this.commitDisableReason = '';
			return true;
		}
		// Disable if invalid clause exists
		const invalid = this.editClauses.find(c => !c.isNew && !c.isValid);
		if (invalid) {
			this.commitDisableReason = 'Invalid expression';
			return true;
		}
		this.commitDisableReason = '';
		return false;
	}

	onSelectCol(idx) {
		const clause = this.editClauses[idx];
		clause.val = null;
		// this.onEditClause(idx);
		if (clause.col.standardName.toLowerCase() === this.discrepancyIdStandardNameLower) {
			clause.val = this.userService.customerInfo.discrepancyId;
			// this.onInputVal(idx, 'input');
			// this.checkClauseValidity_input(idx);
		}
		this.onInputVal(idx);
		// Update nguid valid after onInputVal updating isValid
		if (clause.col && clause.col.standardName === this.nguidStandardName) {
			clause.isValid = true;
		}
	}

	onInputVal(idx, type?: 'select' | 'input') {
		this.onEditClause(idx);
		if (type === 'select') {
			this.checkClauseValidity_domain(idx);
		} else {
			this.checkClauseValidity_input(idx);
		}
	}
	checkClauseValidity_input(idx) {
		const clause = this.editClauses[idx];
		const check = this.editService.checkClauseVal(clause.val, clause.col, this.domainsByColumn);
		clause.isValid = check.isValid;
		if (check.isFieldCalc) {
			const rsp = true; // TODO: await this.editService.sqlValidate(this.activityService.selectedLayer.getValue(), `${check.val} as ${clause.col.name}`);
			if (rsp) {
				clause.isValid = true;
			} else {
				clause.isValid = false;
				this.toastr.warning('Invalid expression')
			}
		}
	}
	private async checkClauseValidity_domain(idx) {
		const clause = this.editClauses[idx];
		if (clause.val || clause.val === 0 || clause.val === null) {
			clause.isValid = true;
		} else {
			clause.isValid = false;
		}
	}
	private onEditClause(idx) {
		if (idx === this.editClauses.length - 1) {
			this.editClauses[idx].isNew = false;
		}
		// this.isValid = false;
	}
	addNewEditClause() {
		this.editClauses.push({
			col: null,
			operator: '=',
			val: '',
			isNew: true
		});
	}

	getPlaceholder(col: BatchEditableColumn) {
		if (this.domainsByColumn[col.prop] && this.domainsByColumn[col.prop].type === 'list') {
			return;
		}
		let str = 'value or expression ';
		if (col.type === 'numeric') {
			str += ' eg. [' + col.prop + '] + 200';
		} else if (col.type === 'text') {
			str += ' eg. [' + col.prop + '] + \'my_string\'';
		}
		return str;
	}
	removeClause(idx: number) {
		this.editClauses.splice(idx, 1);
	}
	discard() {
		this.editClauses = [];
		this.addNewEditClause();
	}
	async commit(nguidOnly?: boolean) {
		const spinnerId = this.commonService.getRandomId();
		this.commonService.setLoadingSpinner(true, spinnerId);
		try {
			await this._commit(nguidOnly);
		} catch (err) {
			console.log(err);
		}
		this.commonService.setLoadingSpinner(false, spinnerId);
	}
	private async _commit(nguidOnly?: boolean) {

		const selectedLayer = this.activityService.selectedLayerBS.getValue().layer;
		const sourceTableName = selectedLayer.category.tableName;
		// Cols
		const prefix = selectedLayer.category.colPrefix;
		const primKeyCol = this.editService.getPrimKeyCol(prefix);
		const cols = this.editService.getUpdateColsInSourceTable(sourceTableName, prefix);

		let whereClause = '';
		if (!this.activityService.sqlForBatch) { // Might be in schema mode, which doesn't have sql_filter. // Need to create sql frontend
			if (selectedLayer.state.num <= 1.51) {
				// WhereClause - attr filter
				const attrColumnFilters = this.attrFilterService.attrFilters.getValue().filters.filter(f => f.filter).map(f => f.filter);
				whereClause = this.editService.getWhereClauseFromColumnFilters(attrColumnFilters);
				// WhereClause - map extent
				// const menu = this.contentLayerService.contentMenuItemKeyBS.getValue()
				// const viewKey: ViewMenuItemKey = this.contentLayerService.viewsBS.getValue()[menu];
			}
		} else {
			whereClause = this.activityService.sqlForBatch;
		}
		const mapFilter = this.activityService.mapFilterBS.getValue();
		// if (viewKey === 'map') {
		if (mapFilter.isOn) {
			const extent = this.mapService.map.olMap.getView().calculateExtent();
			whereClause = this.editService.addToWhereClause(whereClause, `${prefix}_geom && ST_MakeEnvelope (${extent[0]},${extent[1]},${extent[2]},${extent[3]},3857)`);
		}
			// // add layer id
			// const layerIdWhere = `${prefix}_${this.attrIndexService.layerIdColumn} = ${selectedLayer.id}`;
		// whereClause = this.editService.addToWhereClause(whereClause, layerIdWhere);

		if (!whereClause) {
			console.log('something is wrong: no whereClause for batch');
			return;
		}
		// Changed props
		const changedCols: {
			[col: string]: {val: any; isExpression?: boolean}
		} = {};
		const cate = this.activityService.selectedLayerBS.getValue().layer.category;
		const editClauses = nguidOnly ? this.editClauses.filter(c => c.col.standardName === this.nguidStandardName) : this.editClauses;
		editClauses.forEach(clause => {
			if (!clause.isValid) {
				return;
			}
			changedCols[clause.col.prop] = {
				val: null
			}
			const cols = this.columns.map(c => {
				return {
					name: c.name,
					prop: c.prop
				}
			});
			cate.standardCols.forEach(s => {
				if (!cols.find(c => c.prop.toLowerCase() === cate.colPrefix + '_' + s.colName.toLowerCase())) {
					cols.push({ // For columns like globalid // todo: globalid not working, hard code for now
						name: s.colName, // TODO
						prop: cate.colPrefix + '_' + s.colName.toLowerCase()
					})
				}
			});
			if (clause.col.standardName === this.nguidStandardName) {
				const split = '__splitString__';
				let nguidClause = this.agencyInfoService.computeNguid(cate.code, split) //`'urn:emergency:uri:SSAP:' || ${cate.colPrefix}_globalid || ':state.gov'+` 
				const splits = nguidClause.split(split);
				nguidClause = `'${splits[0]}' || ${cate.colPrefix}_globalid || '${splits[1]}'`;
				changedCols[clause.col.prop].val = nguidClause;
				changedCols[clause.col.prop].isExpression = true;
			} else {
				const format = this.editService.formatClauseValForGto(clause.val, clause.col.type, cols); // this.columns
				changedCols[clause.col.prop].val = format.val;
				changedCols[clause.col.prop].isExpression = format.isExpression;
			}


				// if (typeof(clause.val) !== 'string') {
				// 	changedCols[clause.col.prop].val = clause.val;
				// 	return;
				// }
				// // For string clause.val, it could be expression
				// if (clause.col.type === 'numeric') {
				// 	// Replace column in [] to real column name
				// 	const replace = this.editService.replaceColInClause(clause.val);
				// 	changedCols[clause.col.prop].val = replace.txt;
				// 	if (replace.isExpression) {
				// 		changedCols[clause.col.prop].isExpression = true;
				// 	}
				// 	// let strRemain = clause.val;
				// 	// let strFinal = '';
				// 	// const matches = clause.val.match(/\[(.*?)\]/g); // Find columns in []
				// 	// matches.forEach((match, idx) => {
				// 	// 	const colName = match.substring(1, match.length - 1) || ''; // column name user put
				// 	// 	const col = this.columns.find(c => c.name.toLowerCase() === colName.toLowerCase());
				// 	// 	const idxInRemainString = strRemain.indexOf(match);
				// 	// 	strFinal += strRemain.substring(0, idxInRemainString - 1);
				// 	// 	if (col) {
				// 	// 		strFinal += `${col.prop}`;
				// 	// 	} else {
				// 	// 		strFinal += match;
				// 	// 	}
				// 	// 	// Remove match and text before match from remain string
				// 	// 	strRemain = strRemain.substring(idxInRemainString + match.length, strRemain.length);
				// 	// });
				// 	// strFinal += strRemain;
				// 	// changedCols[clause.col.prop].val = strFinal;
				// } else if (clause.col.type === 'text') {
				// 	// Find matchs: [column] + some_string.
				// 			// const matchesWithPlus = clause.val.match(/\[(.*?)\]\s?\+.*?(?=((\[(.*?)\]))|$)/g);
				// 	// let strFinal = clause.val;
				// 	// const regex = /\[(.*?)\]\s?\+.*?(?=((\[(.*?)\]))|$)/;
				// 	// while (regex.exec(strFinal)) {
				// 	// 	regex.exec(strFinal)
				// 	// }
				// 	// // Replace + to ||
				// 	// // Replace some_string to 'some_string'
				// 	// let strRemain = clause.val;
				// 	// // let strFinal = '';
				// 	// matchesWithPlus.forEach(match => {
				// 	// 	const idxInRemainString = strRemain.indexOf(match);
				// 	// 	strFinal += strRemain.substring(0, idxInRemainString - 1);

				// 	// 	// Remove match and text before match from remain string
				// 	// 	strRemain = strRemain.substring(idxInRemainString + match.length, strRemain.length);
				// 	// });

				// 	// Check if [column] is followed by +, if so, replace + with ||, and add quotes to the string after it
				// 	let replace = this.editService.replaceColAndValueInClause(clause.val);
				// 	changedCols[clause.col.prop].val = replace.txt;
				// 	if (replace.isExpression) {
				// 		changedCols[clause.col.prop].isExpression = true;
				// 	}
				// } else {
				// 	changedCols[clause.col.prop].val = clause.val;
				// }
		})
		if (Object.keys(changedCols).length === 0) {
			this.toastr.info('Nothing to update')
			return;
		}
		
		const isArchiveMode = this.archiveModeService.archiveModeBS.getValue().isArchive;
		let gtos = isArchiveMode ? this.editService.getUpdateGtosBatchArchive(sourceTableName, whereClause, changedCols, cols, primKeyCol, prefix).gtos : this.editService.getUpdateGtosBatch(sourceTableName, whereClause, changedCols, cols, primKeyCol, prefix).gtos;
		if (gtos.length) {
			const changedColStrings = Object.keys(changedCols);
				// .map(c => {
				// 	const std = this.mainDataService.schemas.cols.standard.find(s => s.prop === c);
				// 	if (std) {
				// 		return std.info.standardColName;
				// 	} else if (c.split('_')[1] === this.attrIndexService.geomCol) { // Manually add geom col since it's removed from standard cols at beginning
				// 		return this.attrIndexService.geomCol;
				// 	}
				// });
			await this.editService.commitGtoAndQcForBach(gtos, changedColStrings, selectedLayer.id, prefix);

			
			// // Notify agency
			// try {
			// 	if (selectedLayer.category.code === this.layerService.cateCodeMatchPoint) {
			// 		const affectedAgencyIds = [];
			// 		this.editRecordList.forEach(i => {
			// 			const agencyList = i[this.editService.dmAgencyColumn].split(',');
			// 			agencyList.forEach(a => {
			// 				if (a != this.userService.getCustomerIdByType() && affectedAgencyIds.indexOf(a) === -1) {
			// 					affectedAgencyIds.push(a);
			// 				}
			// 			})
			// 		});
			// 		if (affectedAgencyIds.length > 0) {
			// 			const promises = affectedAgencyIds.map(id => this.userService.getCustomerInfo(id, false, false));
			// 			await Promise.all(promises).then(rsp => {
			// 				const affectedNames = rsp.map(i => i.nameShort);
			// 				this.toastr.success('The following agency(s) has been notified of this updates: ' + affectedNames.join(','));
			// 			});
			// 		}
			// 	}
			// } catch (err) {
			// 	console.log(err);
			// }

			// this.clearDataCards();
			// this.toastr.success('Successfully updated.');
			// // Qc
			// let layerId;
			// switch (this.current.contentKey) {
			// 	case 'myLayers':
			// 		layerId = this.selectedLayer.id;
			// 		break;
			// 	case 'collaborative':
			// 		// TODO
			// }
			// const dynamicQcKeyvals = data.dynamicQcKeyvals;
			// const editFilters: QcEditFilter[] = [{
			// 	gly_id: layerId,
			// 	cols: data.changedCols,
			// 	keyvals: dynamicQcKeyvals
			// }];
		}
	}

	computeNguid() {
		this.commit(true);
	}

	ngOnInit() {
		this.nguidStandardName = this.attrIndexService.nguidCol;
		this.discrepancyIdStandardNameLower = this.attrIndexService.discrepancyAgencyIdStandardLower;
		this.selectedFilterType = this.filterTypes[0];

		this.batchFilterCountSub = this.activityService.batchFilterCount.subscribe(data => {
			this.filterTypes.find(t => t.type === 'filter').count = data.count;
		});

		this.addNewEditClause();
	}
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.columns && this.columns) {
			const layerCate = this.activityService.selectedLayerBS.getValue().layer.category;
			const cols: {prop; standard: LayerCategoryStandardSchema}[] = this.columns.map(c => {
				return {
					prop: c.prop,
					standard: layerCate.standardCols.find(s => s.colName === c.standardName)
				}
			});
			this.domainsByColumn = this.domainService.getColumnDomains(cols, layerCate.code);
		}
	}
	ngOnDestroy(): void {
		this.batchFilterCountSub.unsubscribe();
	}
}
